<template>
  <div class="home">
    <a-row :gutter="[16, 16]">
      <a-col :lg="12" :md="24" :sm="24" :xl="8" :xs="24" :xxl="8">
        <a-card :loading="loadingUser">
          <span v-if="account.accountType === 'COMMERCIAL'" class="account-type-badge commercial-badge">
            <a-icon style="margin-right: 15px;" type="bank"/>Konto komercyjne
          </span>
          <span v-if="account.accountType === 'SERVICE'" class="account-type-badge service-badge">
            <a-icon style="margin-right: 15px;" type="api"/>Konto systemowe
          </span>

          <h2 style="margin-bottom: 0px;">{{ account.name }} {{ account.surname }}
            <a-tooltip v-if="account.verified" placement="right">
              <template slot="title">
                Dokument zweryfikowany
              </template>
              <a-icon type="safety-certificate" theme="twoTone"/>
            </a-tooltip>
          </h2>
          <small v-if="account.accountStatus">
            <a-badge :status="statusDictionary[account.accountStatus].badge"/>
            {{
              statusDictionary[account.accountStatus].name
            }}</small>
        </a-card>
        <a-card :loading="loadingUser" class="details">
          <div v-if="account.mergedToAccount && account.mergedToAccount !== ''" class="detail-item">
            <a-button type="primary" block @click="goTo({ name: 'User', params: { uuid: account.mergedToAccount } })">Przejdź do nowego konta</a-button>
          </div>
          <div class="detail-item">
            <small>Notatki</small>
            <AccountFieldStringArea field="NOTES" :value="account.notes" placeholder="Notatki" :account-uuid="account.accountUuid"
                                @updated="updateFieldValueWithoutRefresh">
              <div class="table-details">
                <vue-markdown v-if="notesShow" class="note-body">{{ account.notes ? account.notes : 'Brak notatek' }}</vue-markdown>
                <div style="padding: 2em; text-align: center" v-if="!notesShow">
                  <a-spin></a-spin>
                </div>
              </div>
            </AccountFieldStringArea>
          </div>
          <a-divider :style="{ marginTop: account.notes ? '1em' : '0px' }" orientation="left">{{
              account.accountType ===
              'PERSONAL' ? 'Dane osobowe' : 'Informacje'
            }}
          </a-divider>
          <div v-if="account.accountStatus === 'BANNED'" class="detail-item">
            <small>Powód blokady</small>
            <div class="table-details">
              <vue-markdown class="note-body">{{ account.lockReason ? account.lockReason : '- brak -' }}</vue-markdown>
            </div>
          </div>
          <div v-if="account.accountType === 'PERSONAL'" class="detail-item">
            <small>PESEL/Numer paszportu</small>
            <AccountFieldString field="PERSON_ID" :value="account.personId" placeholder="PESEL/Numer paszportu" :account-uuid="account.accountUuid"
                                @updated="updateFieldValueWithoutRefresh">
              {{ account.personId ? account.personId : '- brak -' }}
            </AccountFieldString>
            <a-alert :message="personIdVerificationDictionary[account.personIdValidation.result].text"
                     :type="personIdVerificationDictionary[account.personIdValidation.result].type"
                     show-icon>
              <template v-if="account.personIdValidation.gender" #description>
                {{ genderDictionary[account.personIdValidation.gender] }} &bull; {{ formatDate(account.personIdValidation.dateOfBirth) }}
              </template>
            </a-alert>
          </div>
          <div v-if="account.accountType === 'COMMERCIAL'" class="detail-item">
            <small>NIP</small>
            <AccountFieldString field="NIP" :value="account.nip" placeholder="NIP" :account-uuid="account.accountUuid"
                                @updated="updateFieldValueWithoutRefresh">
              {{ account.nip ? account.nip : '- brak -' }}
            </AccountFieldString>
          </div>
          <div class="detail-item">
            <small>{{ account.accountType === 'PERSONAL' ? 'Adres zameldowania' : 'Adres' }}</small>
            <AccountFieldString field="HOME_ADDRESS" :value="account.homeAddress" placeholder="Adres" :account-uuid="account.accountUuid"
                                @updated="updateFieldValueWithoutRefresh">
              {{ account.homeAddress ? account.homeAddress : '- brak -' }}
            </AccountFieldString>
          </div>
          <div class="detail-item">
            <small>Kraj</small>
            <AccountFieldString field="COUNTRY" :value="account.country" placeholder="Kraj" :account-uuid="account.accountUuid"
                                @updated="updateFieldValueWithoutRefresh">
              {{ account.country ? account.country : '- brak -' }}
            </AccountFieldString>
          </div>

          <a-divider orientation="left">Lokalizacja</a-divider>
          <div class="detail-item">
            <small>Lokalizacja</small>
            {{ account.dorm ? account.dorm.name : '- brak -' }}
          </div>
          <div class="detail-item">
            <small>Pomieszczenie</small>
            <AccountFieldString field="ROOM" :value="account.room" placeholder="Pomieszczenie" :account-uuid="account.accountUuid"
                                @updated="updateFieldValueWithoutRefresh">
              {{ account.room ? account.room : '- brak -' }}
            </AccountFieldString>
          </div>

          <div v-if="account.accountType === 'PERSONAL'">
            <a-divider orientation="left">Edukacja</a-divider>
            <div class="detail-item">
              <small>Instytucja</small>
              <AccountFieldString field="UNIVERSITY" :value="account.university" placeholder="Instytucja" :account-uuid="account.accountUuid"
                                  @updated="updateFieldValueWithoutRefresh">
                {{ account.university ? account.university : '- brak -' }}
              </AccountFieldString>
            </div>
            <div class="detail-item">
              <small>Kierunek</small>
              <AccountFieldString field="FACULTY" :value="account.faculty" placeholder="Kierunek" :account-uuid="account.accountUuid"
                                  @updated="updateFieldValueWithoutRefresh">
                {{ account.faculty ? account.faculty : '- brak -' }}
              </AccountFieldString>
            </div>
            <div class="detail-item">
              <small>Semestr</small>
              <AccountFieldString field="SEMESTER" :value="account.semester" placeholder="Semestr" :account-uuid="account.accountUuid"
                                  @updated="updateFieldValueWithoutRefresh">
                {{ account.semester ? account.semester : '- brak -' }}
              </AccountFieldString>
            </div>
            <div class="detail-item">
              <small>Numer indeksu</small>
              <AccountFieldString field="STUDENT_ID" :value="account.studentId" placeholder="Numer indeksu" :account-uuid="account.accountUuid"
                                  @updated="updateFieldValueWithoutRefresh">
                {{ account.studentId ? account.studentId : '- brak -' }}
              </AccountFieldString>
            </div>
          </div>


          <a-divider orientation="left">Inne</a-divider>
          <div class="detail-item">
            <small>Adres e-mail</small>
            <AccountFieldString field="EMAIL_ADDRESS" :value="account.emailAddress" placeholder="Adres e-mail" :account-uuid="account.accountUuid"
                                @updated="updateFieldValueWithoutRefresh">
              {{ account.emailAddress ? account.emailAddress : '- brak -' }}
            </AccountFieldString>
          </div>
          <div class="detail-item">
            <small>Chatwoot</small>
            <AccountFieldString field="EXTERNAL_SUPPORT_SYSTEM_ID" :value="account.externalSupportSystemId" placeholder="Chatwoot ID"
                                :account-uuid="account.accountUuid"
                                @updated="updateFieldValueWithoutRefresh">
              <a target="_blank" v-if="account.externalSupportSystemContact && account.externalSupportSystemUrl" :href="account.externalSupportSystemUrl">
                <a-avatar size="small" icon="user" :src="account.externalSupportSystemContact.payload.thumbnail"/>
                {{ account.externalSupportSystemContact ? account.externalSupportSystemContact.payload.name : '- brak -' }}
                <a-icon type="link"/>
              </a>
              <span v-else>- brak -</span>
            </AccountFieldString>
          </div>
          <div v-if="account.created" class="detail-item">
            <small>Data rejestracji</small>
            {{ new Date(account.created) | moment('HH:mm - DD.MM.YYYY') }} r.
          </div>
          <div v-if="account.activationCode" class="detail-item">
            <small>Kod aktywacyjny</small>
            {{ account.activationCode }}
          </div>
          <div v-if="account.loginType" class="detail-item">
            <small>Typ logowania</small>
            {{ loginTypeDictionary[account.loginType] }}
          </div>
          <div class="detail-item">
            <small>Hasło jednorazowe</small>
            <div class="table-details">
              <a-row>
                <a-col :span="12">
                  <p v-if="account.loginType === 'CODE'" style="margin-top: 2px; margin-bottom: 0px;">
                    {{ formatOneTimePassword(account.oneTimePassword) }}
                  </p>
                  <p v-else style="margin-top: 2px; margin-bottom: 0px;">
                    - brak -
                  </p>
                </a-col>
                <a-col :span="12" style="text-align: right;">
                  <a-button icon="reload" size="small" type="primary" @click="resetPassword"></a-button>
                </a-col>
              </a-row>
            </div>
          </div>
          <div v-if="account.tags && account.tags.length > 0" class="detail-item">
            <small>Tagi</small>
            <a-tag v-for="(tag, index) in account.tags" :key="index" :color="tag.color">{{ tag.name }}</a-tag>
          </div>
        </a-card>
      </a-col>

      <a-col :lg="12" :md="24" :sm="24" :xl="16" :xs="24" :xxl="16">
        <a-card>
          <a-space>
            <a-button-group>
              <a-button type="primary" v-if="account.accountStatus !== 'ACTIVE'" @click="activateAccount" icon="check">Aktywuj</a-button>
              <a-button type="default" v-if="account.accountStatus !== 'INACTIVE'" @click="openMergeModal" icon="apartment">Połącz konta</a-button>
              <a-dropdown v-if="account.accountStatus !== 'BANNED'" icon="stop">
                <a-menu slot="overlay" @click="deactivateAccount">
                  <a-menu-item key="dane">
                    Nieprawidłowe dane
                  </a-menu-item>
                  <a-menu-item key="regulamin">
                    Nieprzestrzeganie regulaminu
                  </a-menu-item>
                </a-menu>
                <a-button>
                  <a-icon type="stop"/>
                  Zablokuj
                  <a-icon type="down"/>
                </a-button>
              </a-dropdown>
            </a-button-group>
            <a-dropdown>
              <a-menu slot="overlay" @click="handleMenuClick">
                <a-menu-item key="bulk">
                  <a-icon type="unordered-list"/>
                  Dodaj urządzenia
                </a-menu-item>
                <a-menu-item key="issue">
                  <a-icon type="message"/>
                  Dodaj zgłoszenie wewnętrzne
                </a-menu-item>
                <a-menu-divider/>
                <a-menu-item key="pair" :disabled="account.externalSupportSystemId === undefined || account.externalSupportSystemId === ''">
                  <a-icon type="sync"/>
                  Synchronizuj z Chatwoot
                </a-menu-item>
              </a-menu>
              <a-button> Więcej
                <a-icon type="down"/>
              </a-button>
            </a-dropdown>
          </a-space>
        </a-card>

        <a-card :loading="loadingUser" style="margin-top: 16px; margin-bottom: 16px">
          <a-row>
            <a-col :span="24">
              <a-button-group>
                <a-button @click="activeTabIndex = '1'">
                  Lista urządzeń
                </a-button>
                <a-button :disabled="account.externalSupportSystemId === undefined || account.externalSupportSystemId === ''" @click="activeTabIndex = '2'">
                  Chatwoot
                  <a-badge v-if="openIssues" :count="openIssues" style="margin-left: 6px; margin-bottom: 2px;"/>
                </a-button>
              </a-button-group>
            </a-col>
          </a-row>
        </a-card>

        <a-row v-if="activeTabIndex === '1'">
          <a-col :span="24">
            <a-row v-if="account.devices !== undefined" :gutter="[16, 16]">
              <a-col v-for="device in account.devices" :key="device.deviceUuid" :span="24">
                <Device :device="device" :dorms="dorms" @updated="reloadDevices" @changedOwner="redirectToUserProfile"/>
              </a-col>
            </a-row>
            <a-empty v-if="(account.devices === undefined || account.devices.length === 0) && !loadingUser" style="margin-top: 3em;">
                <span slot="description" style="color: rgba(0, 0, 0, 0.65)">
                  <strong>{{ account.name }} nie posiada ani jednego urządzenia!</strong>
                  <p>Dodaj nowe z poziomu górnej belki</p>
                </span>
            </a-empty>
          </a-col>
        </a-row>

        <a-row v-if="activeTabIndex === '2'">
          <a-col :span="24">
            <a-row v-if="issues !== []" :gutter="[16, 16]">
              <a-col v-for="issue in issues" :key="issue.id" :span="24">
                <a-card>
                  <span slot="title">
                    {{
                      issue.additional_attributes.chat_id ? `Chat #${issue.additional_attributes.chat_id}` : `Temat: ${issue.additional_attributes.mail_subject}`
                    }}
                  </span>
                  <a slot="extra" href="#">
                    <a-icon type="link"/>
                  </a>
                  <a-row :gutter="[8, 8]">
                    <a-col :lg="12" :md="24" :sm="24" :xl="12" :xs="24" :xxl="6">
                      <small style="display: block;">Status zgłoszenia</small>
                      <a-badge :status="issueStatusColor[issue.status]"/>
                      {{ issueStatus[issue.status] }}
                    </a-col>
                    <a-col :lg="12" :md="24" :sm="24" :xl="12" :xs="24" :xxl="6">
                      <small style="display: block;">Kanał komunikacji</small>
                      {{ issue.meta.channel.replace('Channel::', '') }}
                    </a-col>
                    <a-col :lg="12" :md="24" :sm="24" :xl="12" :xs="24" :xxl="6">
                      <small style="display: block;">Data utworzenia</small>
                      {{ formatUnixTimestamp(issue.created_at) }}
                    </a-col>
                    <a-col :lg="12" :md="24" :sm="24" :xl="12" :xs="24" :xxl="6">
                      <small style="display: block;">Tagi</small>
                      <a-tag v-for="label in issue.labels" :key="label" :color="labelsColors[label] ?? ''">
                        {{ label }}
                      </a-tag>
                    </a-col>
                    <a-col :lg="12" :md="24" :sm="24" :xl="12" :xs="24" :xxl="6">
                      <small style="display: block;">Autor ostatniej wiadomości</small>
                      {{ issue.messages[0] && issue.messages[0].sender ? issue.messages[0].sender.name : '- brak -' }}
                    </a-col>
                    <a-col :lg="24" :md="24" :sm="24" :xl="24" :xs="24" :xxl="24">
                      <small style="display: block;">Ostatnia wiadomość</small>
                      {{ issue.messages[0] ? issue.messages[0].content : '- brak -' }}
                    </a-col>
                  </a-row>
                </a-card>
              </a-col>
            </a-row>
            <a-empty v-if="issues === []" style="margin-top: 3em;">
              <span slot="description">
                <strong>{{ account.name }} nie posiada żadnych zgłoszeń!</strong>
                <p v-if="account.externalSupportSystemUrl">Połącz konto z kontaktem na Chatwoot</p>
              </span>
            </a-empty>
          </a-col>
        </a-row>
      </a-col>
    </a-row>
    <BulkAddModal :show="bulkAddModal" @close="bulkAddModalClose"/>
    <MergeAccountsModal :show="mergeToModal" :account-uuid="account.accountUuid" :account-name="account.name + ' ' + account.surname" @close="mergeToModalClose" @merged="mergedAccounts"/>
    <CreateIssueModal :show="createIssueModal" :account-uuid="account.accountUuid" :user-dorm-code="account.dorm.code" :user-room="account.room" @close="closeIssueModal"/>
  </div>
</template>


<script>
import httpClient from "../utils/httpClient";
import Device from '../components/device/Device.vue';
import VueMarkdown from "vue-markdown";
import BulkAddModal from "@/components/device/BulkAddModal.vue";
import MergeAccountsModal from "@/components/account/MergeAccountsModal.vue";
import AccountFieldString from "@/components/account/AccountFieldString.vue";
import AccountFieldStringArea from "@/components/account/AccountFieldStringArea.vue";
import CreateIssueModal from "@/components/issue/CreateIssueModal.vue";

const statusDictionary = {
  "INACTIVE": {badge: "default", name: "Nieaktywny"},
  "WAITING": {badge: "warning", name: "Weryfikacja dokumentu"},
  "DOCUMENT_APPROVED": {badge: "processing", name: "Oczekuje na aktywację"},
  "ACTIVE": {badge: "success", name: "Aktywny"},
  "BANNED": {badge: "error", name: "Zablokowany"},
  "DEPRECATED": {badge: "default", name: "Wygasły"},
}

const loginTypeDictionary = {
  "NAME_AND_SURNAME": "Imię i nazwisko",
  "CODE": "Kod jednorazowy",
  "PASSWORD": "Hasło",
  "FIDO": "Klucz FIDO",
}

const issueStatus = {
  "open": "Otwarte",
  "resolved": "Rozwiązane",
  "snoozed": "Uśpione",
  "pending": "Oczekujące",
}

const issueStatusColor = {
  "open": "processing",
  "resolved": "success",
  "snoozed": "warning",
  "pending": "processing",
}

const labelsColors = {
  "asystent": "blue",
  "bartek": "orange",
  "bursa": "cyan",
  "filon": "purple",
  "laura": "pink",
  "mimoza": "red",
  "proton": "green",
  "telegram": "#2db7f5",
  "email": "#87d068",
}

const genderDictionary = {
  "MALE": "Mężczyzna",
  "FEMALE": "Kobieta",
}

const personIdVerificationDictionary = {
  "VALID_PESEL": {
    "color": "#52c41a",
    "text": "Numer PESEL poprawny",
    "icon": "check-circle",
    "type": "success"
  },
  "VALID_PASSPORT": {
    "color": "#52c41a",
    "text": "Numer paszportu poprawny",
    "icon": "check-circle",
    "type": "success"
  },
  "INVALID_PESEL_FORMAT": {
    "color": "#faad14",
    "text": "Niepoprawny format numeru PESEL",
    "icon": "warning",
    "type": "warning"
  },
  "INVALID_PASSPORT_FORMAT": {
    "color": "#faad14",
    "text": "Niepoprawny format numeru paszportu",
    "icon": "warning",
    "type": "warning"
  },
  "INVALID_PESEL_DATE": {
    "color": "#f5222d",
    "text": "Niepoprawna data urodzenia",
    "icon": "exclamation-circle",
    "type": "error"
  },
  "INVALID_PESEL_CHECKSUM": {
    "color": "#f5222d",
    "text": "Niepoprawna suma kontrolna numeru PESEL",
    "icon": "exclamation-circle",
    "type": "error"
  },
  "INVALID_FORMAT": {
    "color": "#faad14",
    "text": "Niepoprawny format",
    "icon": "warning",
    "type": "warning"
  }
}

const tabList = [
  {
    key: 'article',
    tab: 'article',
  },
  {
    key: 'app',
    tab: 'app',
  },
  {
    key: 'project',
    tab: 'project',
  },
]

const semestersDictionary = [
  {key: 1, value: "pierwszy"},
  {key: 2, value: "drugi"},
  {key: 3, value: "trzeci"},
  {key: 4, value: "czwarty"},
  {key: 5, value: "piąty"},
  {key: 6, value: "szósty"},
  {key: 7, value: "siódmy"},
  {key: 8, value: "ósmy"},
  {key: 9, value: "dziewiąty"},
  {key: 10, value: "dziesiąty"},
  {key: 11, value: "jedenasty"},
  {key: 12, value: "dwunasty"},
  {key: 13, value: "trzynasty"},
  {key: 14, value: "czternasty"},
  {key: 15, value: "piętnasty"},
  {key: 16, value: "inny"},
  {key: 0, value: "nie dotyczy"},
];

export default {
  name: "User",
  components: {
    AccountFieldStringArea,
    CreateIssueModal,
    AccountFieldString,
    MergeAccountsModal,
    Device,
    BulkAddModal,
    "vue-markdown": VueMarkdown,
  },
  data() {
    return {
      statusDictionary,
      loginTypeDictionary,
      semestersDictionary,
      tabList,
      issueStatus,
      issueStatusColor,
      labelsColors,
      genderDictionary,
      personIdVerificationDictionary,
      activeTabIndex: "1",
      loadingUser: true,
      account: {},
      issues: [],
      dorms: [],
      bulkAddModal: false,
      mergeToModal: false,
      editFields: {
        personId: false,
        homeAddress: false,
        country: false,
        university: false,
        faculty: false,
        semester: false,
        studentId: false,
        emailAddress: false,
        notes: false,
        externalSupportSystemContact: false
      },
      loadingFields: {
        personId: false
      },
      createIssueModal: false,
      notesShow: true
    };
  },
  watch: {
    '$route.params.uuid'() {
      this.loadUser()
      this.loadDorms()
      this.$emit("loadedView");
    }
  },
  mounted() {
    this.loadUser()
    this.loadDorms()
    this.$emit("loadedView");
  },
  computed: {
    openIssues() {
      return this.issues.filter(issue => issue.status !== "resolved").length
    }
  },
  methods: {
    loadUser() {
      this.loadingUser = true;
      httpClient
          .get(`/admin/accounts/details/${this.$route.params.uuid}`)
          .then((response) => {
            this.account = response;
            if (this.account.externalSupportSystemUrl && this.account.externalSupportSystemContact) {
              this.getConversations()
            }
            this.saveVisitToLocalStorage();
          }).finally(() => {
        this.loadingUser = false;
      });
    },

    activateAccount() {
      this.$message.loading({content: `Aktywowanie konta ${this.account.name} ${this.account.surname}...`, key: 'activation'});
      httpClient
          .put(`/admin/accounts/details/${this.account.accountUuid}/activate`)
          .then(() => {
            this.loadUser()
            this.$message.success({content: `Konto ${this.account.name} ${this.account.surname} zostało aktywowane!`, key: 'activation'});
          }).catch(() => {
        this.$message.error({content: `Wystąpił błąd z aktywacją ${this.account.name} ${this.account.surname}!`, key: 'activation'});
      })
    },

    deactivateAccount(option) {
      console.log(option)
      this.$message.loading({content: `Blokowanie konta ${this.account.name} ${this.account.surname}...`, key: 'deactivation'});

      var reason = ""
      switch (option.key) {
        case "dane":
          reason = "Podejrzewamy, że dane podane w formularzu mogą być nieprawdziwe. Prosimy o kontakt."
          break;
        case "regulamin":
          reason = "Konto zostało zablokowane z powodu naruszenia regulaminu."
          break;
        default:
          reason = "Prosimy o kontakt."
      }

      httpClient
          .put(`/admin/accounts/details/${this.account.accountUuid}/deactivate`, {reason: reason})
          .then(() => {
            this.loadUser()
            this.$message.success({content: `Konto ${this.account.name} ${this.account.surname} zostało zablokowane!`, key: 'deactivation'});
          }).catch(() => {
        this.$message.error({content: `Wystąpił błąd z blokowaniem ${this.account.name} ${this.account.surname}!`, key: 'deactivation'});
      })
    },

    pairWithExternalSupportSystem() {
      if (!this.account.externalSupportSystemUrl) {
        this.$message.warning("Konto nie posiada przypisanego identyfikatora systemu zewnętrznego!")
        return
      }
      this.$message.loading({content: `Synchronizowanie konta ${this.account.name} ${this.account.surname} z Chatwoot...`, key: 'pairing'});
      httpClient
          .put(`/admin/accounts/details/${this.account.accountUuid}/pair-with-external-support-system`)
          .then(() => {
            this.loadUser()
            this.$message.success({content: `Konto ${this.account.name} ${this.account.surname} zostało zsynchronizowane z Chatwoot!`, key: 'pairing'});
          }).catch(() => {
        this.$message.error({content: `Wystąpił błąd z synchronizacją ${this.account.name} ${this.account.surname}!`, key: 'pairing'});
      })
    },

    getConversations() {
      httpClient
          .get(`/admin/accounts/details/${this.account.accountUuid}/issues`)
          .then((response) => {
            if (response === null) {
              return
            }
            this.issues = response.payload.reverse();
            this.issues.forEach(issue => {
              issue.labels.push(issue.meta.channel.replace('Channel::', '').toLowerCase())
            })
          }).catch(() => {
        this.$message.error({content: `Wystąpił błąd z pobraniem konwersacji dla ${this.account.name} ${this.account.surname}!`, key: 'conversations'});
      })
    },

    updateFieldValueWithoutRefresh() {
      this.notesShow = false
      httpClient
          .get(`/admin/accounts/details/${this.$route.params.uuid}`)
          .then((response) => {
            this.account = response;
            if (this.account.externalSupportSystemUrl && this.account.externalSupportSystemContact) {
              this.getConversations()
            }
          }).finally(() => {
            setTimeout(() => {
              this.notesShow = true
            }, 200)
      });
    },

    loadDorms() {
      httpClient
          .get(`/admin/dorms`)
          .then((response) => {
            this.dorms = response;
          }).catch((error) => {
        if (error.response.status !== 401) {
          this.$message.error("Wystąpił błąd z pobraniem listy lokalizacji!")
        }
      }).finally(() => {
        this.loadingUser = false;
      });
    },

    resetPassword() {
      httpClient
          .put(`/admin/accounts/details/${this.account.accountUuid}/reset-password`)
          .then(() => {
            this.updateFieldValueWithoutRefresh()
            this.$message.success({content: `Hasło konta ${this.account.name} ${this.account.surname} zostało zresetowane!`, key: 'resetPassword'});
          }).catch(() => {
        this.$message.error({content: `Wystąpił błąd z blokowaniem ${this.account.name} ${this.account.surname}!`, key: 'resetPassword'});
      })
    },

    reloadDevices() {
      this.updateFieldValueWithoutRefresh()
    },

    closeIssueModal() {
      this.createIssueModal = false
    },

    redirectToUserProfile(event) {
      this.loadUser()

      const key = `open${Date.now()}`;
      this.$notification.open({
        message: 'Urządzenie przeniesione!',
        description:
            'Czy chcesz przejść do profilu nowego właściciela urządzenia?',
        duration: 15,
        btn: h => {
          return h(
              'a-button',
              {
                props: {
                  type: 'primary',
                  size: 'small',
                },
                on: {
                  click: () => {
                    this.$notification.close(key)
                    this.$router.push({name: "User", params: {uuid: event}})
                    this.$message.success("Załadowano profil nowego właściciela!")
                  },
                },
              },
              'Przejdź',
          );
        },
        key,
        onClose: close,
      });
    },

    goTo(obj) {
      this.$router.push(obj);
    },

    handleMenuClick(event) {
      console.log("event: ", event)
      if (event.key === 'bulk') {
        this.bulkAddModal = true
      } else if (event.key === 'pair') {
        this.pairWithExternalSupportSystem()
      } else if (event.key === 'issue') {
        this.createIssueModal = true
      } else {
        this.$message.warning("Funkcjonalność w przygotowaniu!")
      }
    },

    openMergeModal() {
      this.mergeToModal = true
    },

    bulkAddModalClose() {
      this.bulkAddModal = false
      this.reloadDevices()
    },

    mergeToModalClose() {
      this.mergeToModal = false
    },

    mergedAccounts(event) {
      console.log("Redirect to:", event)
      this.mergeToModal = false
      this.$router.push({name: "User", params: {uuid: event}})
      window.location.reload();
    },

    formatOneTimePassword(str) {
      let middle = Math.floor(str.length / 2)
      return str.substring(0, middle) + '-' + str.substring(middle)
    },

    formatUnixTimestamp(unixTimestamp) {
      const date = new Date(unixTimestamp * 1000);

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${hours}:${minutes} - ${day}.${month}.${year} r.`;
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}.${month}.${year} r.`;
    },

    saveVisitToLocalStorage() {
      const visitedProfiles = JSON.parse(localStorage.getItem('visitedProfiles')) || [];
      const existingProfileIndex = visitedProfiles.findIndex(account => account.accountUuid === this.account.accountUuid);

      if (existingProfileIndex === -1) {
        visitedProfiles.push({
          accountUuid: this.account.accountUuid,
          status: this.account.accountStatus,
          name: `${this.account.name} ${this.account.surname}`,
          identificationId: this.account.accountType === 'PERSONAL' ? this.account.personId : this.account.nip,
          dorm: this.account.dorm,
          lastVisited: new Date().getTime()
        });
      }

      visitedProfiles.sort((a, b) => b.lastVisited - a.lastVisited);

      if (visitedProfiles.length > 5) {
        visitedProfiles.splice(5);
      }

      console.log("visitedProfiles", visitedProfiles)
      localStorage.setItem('visitedProfiles', JSON.stringify(visitedProfiles));
    }
  },
};
</script>

<style scoped>
.table-details {
  background-color: rgba(200, 200, 200, 0.1);
  padding: 0.5em 1em;
  margin-right: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
}

.legend-item {
  margin-left: 1.4em;
}

.details {
  margin-top: 16px;
}

.details .detail-item {
  margin-top: 1em;
}

.details .detail-item:first-child {
  margin-top: 0em;
}

.detail-item small {
  display: block;
}

.account-type-badge {
  display: block;
  border-radius: 5px;
  padding: 6px 16px;
  margin-bottom: 1em;
  font-size: 0.9em;
  text-shadow: 0px 1px 3px rgba(0, 0, 0, 0.2);
}

.commercial-badge {
  background-color: #8a0064;
  border: 1px solid rgb(75, 0, 57);
  color: rgb(255, 194, 241);
}

.service-badge {
  background-color: #00808a;
  border: 1px solid rgb(9, 82, 87);
  color: rgb(194, 242, 255);
}

.note-body {
  margin-top: 1em;
}

.note-body blockquote {
  border-left: 3px solid #99d9ff;
  padding-left: 0.6em;
}

.transparent-card {
  background-color: transparent;
  box-shadow: none;
  border: none;
}
</style>

<style>
.transparent-card .ant-card-head {
  border: none;
  padding: 0 12px;
}
</style>
